import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DragOverlay } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import TaskCard from '../tasks/TaskCard';
import TaskCardGhost from '../tasks/TaskCard/TaskCardGhost';
import GoalActiveTasksSection from './GoalActiveTasksSection';
import GoalCompletedTasksSection from './GoalCompletedTasksSection';
import { motion, useAnimationControls } from 'framer-motion';

interface GoalTaskBoardContentProps {
  activeTasks: Task[];
  completedTasks: Task[];
  activeTask: Task | null;
  setActiveTask: (task: Task | null) => void;
  taskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  // Function to open page creation modal with task data
  onGenerateContent?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open research creation modal with task data
  onGenerateResearch?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open image creation modal with task data
  onGenerateImage?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Loading state for content generation
  isGeneratingContent?: boolean;
  // Function to open task creation modal
  onCreateTask?: () => void;
  // NEW: Assignee filter props
  selectedAssigneeId?: string;
  onAssigneeChange?: (assigneeId: string) => void;
  teamMembers?: any[];
  isLoadingTeam?: boolean;
  assigneeTaskCounts?: {
    assigneeCounts: Record<string, number>;
    totalTasks: number;
    unassignedCount: number;
  };
  // NEW: Ref to the CreateTaskPanel for scrolling
  createTaskPanelRef?: React.RefObject<HTMLDivElement>;
  workbookId?: string; // For navigation to generated content
}

const GoalTaskBoardContent: React.FC<GoalTaskBoardContentProps> = ({ 
  activeTasks, 
  completedTasks, 
  activeTask, 
  setActiveTask,
  taskOperations,
  onGenerateContent,
  onGenerateResearch,
  onGenerateImage,
  isGeneratingContent,
  onCreateTask,
  selectedAssigneeId,
  onAssigneeChange,
  teamMembers,
  isLoadingTeam,
  assigneeTaskCounts,
  createTaskPanelRef,
  workbookId
}) => {
  const controls = useAnimationControls();
  
  // Clear active task after animation completes
  useEffect(() => {
    if (activeTask) {
      // Reset animation when a new active task is set
      controls.set("initial");
      controls.start("animate");
    }
  }, [activeTask, controls]);

  return (
    <>
      <div className="space-y-3 md:space-y-6">
        {/* Goal Active Tasks Section */}
        <GoalActiveTasksSection 
          tasks={activeTasks} 
          taskOperations={taskOperations}
          onGenerateContent={onGenerateContent}
          onGenerateResearch={onGenerateResearch}
          onGenerateImage={onGenerateImage}
          isGeneratingContent={isGeneratingContent}
          onCreateTask={onCreateTask}
          createTaskPanelRef={createTaskPanelRef}
          selectedAssigneeId={selectedAssigneeId}
          onAssigneeChange={onAssigneeChange}
          teamMembers={teamMembers}
          isLoadingTeam={isLoadingTeam}
          assigneeTaskCounts={assigneeTaskCounts}
          workbookId={workbookId}
        />

        {/* Goal Completed Tasks Section */}
        <GoalCompletedTasksSection 
          tasks={completedTasks}
          taskOperations={taskOperations}
          // Note: Clear completed tasks functionality can be added later if needed
        />
      </div>

      {/* Portal DragOverlay to document.body to escape transform context */}
      {typeof window !== 'undefined' && createPortal(
        <DragOverlay
          dropAnimation={null}
          modifiers={[]}
          zIndex={1000}
        >
          {activeTask ? (
            <TaskCardGhost task={activeTask} />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </>
  );
};

export default GoalTaskBoardContent; 